import {
  getDiscountDefault,
  handleApplyDiscount as handleApplyToDiscount,
} from 'components/Discount/func';
import { getIsDefaultAutoApplyPending } from 'components/Discount/components/DiscountsDrawer/isDefaultAutoApplyPending';
import useCalculatorRoundingStore from 'features/calculatorRounding/hooks/useCalculatorRoundingStore';
import useFormAddTransactionStore from 'hooks/form-add-transaction/useFormAddTransactionStore';
import useFxDiscount from 'hooks/fx/useFxDiscount';
import useFxFormAddTransactionStore from 'hooks/fx/useFxFormAddTransactionStore';
import useAuth from 'hooks/useAuth';
import usePage from 'hooks/usePage';
import { isEmpty } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';

const withDiscountAutoApply = (Component) => {
  const DiscountAutoApplyComponent = (props) => {
    const { autoApplyPolicy, failedDiscountRecovery, isDisabled, onApplyError } =
      props || {};

    const { token } = useAuth();
    const { isAddTransactionPage } = usePage();
    const { isCalculatorRounding } = useCalculatorRoundingStore();

    const discountApplyRef = useRef(false);

    const {
      paymentMethod: fxPaymentMethod,
      addTransactionInformation: fxInformation,
    } = useFxFormAddTransactionStore();
    const { handleApplyFxDiscount } = useFxDiscount();

    const {
      paymentMethod: toPaymentMethod,
      addTransactionInformation: toInformation,
    } = useFormAddTransactionStore();

    const {
      discountsAvailable,
      discountsInactive,
      discountApplied,
    } = (isAddTransactionPage ? toPaymentMethod : fxPaymentMethod) || {};
    // The raw active list now lives at the top level of addTransactionInformation
    // (a sibling of paymentMethod), so read it from there rather than from
    // paymentMethod, which step navigation replaces.
    const { discountsActive } =
      (isAddTransactionPage ? toInformation : fxInformation) || {};
    const activeList = Array.isArray(discountsActive) ? discountsActive : [];
    // Once any discount is hard-invalidated at Review & Pay this transaction, stop
    // auto-applying defaults entirely: the user then chooses manually from the
    // other offers instead of having a different default auto-applied. The
    // `hardInvalid` marker lives on discountsActive (re-stamped on every fetch)
    // and is cleared only at the new-transaction reset.
    const hasHardInvalidated = activeList.some((d) => d?.hardInvalid);
    // Add-transaction (TO) flow: auto-apply the FIRST default in the raw active
    // list (server order), regardless of amount match, so the first default is
    // the one validated. If it doesn't qualify (e.g. amount below minTxnAmount)
    // the server rejects it -> failure toast -> auto-apply stops, instead of
    // silently skipping to a later matching default. FX flow keeps prior
    // behaviour.
    const discountDefault = isAddTransactionPage
      ? hasHardInvalidated
        ? null
        : activeList.find((d) => d?.isDefault && !d?.hardInvalid) || null
      : getDiscountDefault({ discountsAvailable, discountsInactive });

    const handleApplyDiscount =
      props.handleApplyDiscount ||
      (isAddTransactionPage ? handleApplyToDiscount : handleApplyFxDiscount);

    const isAutoApplyReady =
      autoApplyPolicy?.isReady ?? props.isAutoApplyReady;
    const isAutoApplyBlocked =
      autoApplyPolicy?.isBlocked ?? props.isAutoApplyBlocked;
    const handleAutoApplyError = autoApplyPolicy?.onApplyError || onApplyError;

    const hasLoadedDiscounts =
      !isEmpty(discountsAvailable) || !isEmpty(discountsInactive);

    // A default discount is going to be auto-applied, but the validate/apply
    // call has not resolved yet. Derived from the same store-backed signals
    // that decide auto-apply (rather than a caller-side heuristic) so the
    // drawer can show only its loading placeholder for the whole window
    // (loaded -> applying -> resolved) instead of briefly flashing discount
    // content before the default apply lands.
    // A resolved failure means discount validation/apply has already completed
    // (unsuccessfully). Mirror the parent's `!failedDiscount` guard so the
    // pending window closes on failure too. Without this the pending stays true
    // forever after a failed auto-apply (default stays non-empty and nothing is
    // applied), which keeps isValidating true, holds the loading background on,
    // and prevents the failure result from ever surfacing. It also stops the
    // arm effect from auto-re-applying a discount that already failed; a manual
    // retry still goes through failedDiscountRecovery.onRetry.
    const hasResolvedFailure = Boolean(failedDiscountRecovery?.discount);
    const isDefaultAutoApplyPending = getIsDefaultAutoApplyPending({
      discountsLoaded: hasLoadedDiscounts,
      hasDefault: !isEmpty(discountDefault),
      isApplied: !isEmpty(discountApplied),
      hasFailure: hasResolvedFailure,
      isBlocked: isAutoApplyBlocked,
      isDisabled,
      isReady: isAutoApplyReady !== false,
    });

    // Defer the default auto-apply past the initial render that makes it
    // pending, so the loading placeholder is committed before any
    // discount-validate call fires. This keeps a validate call from running on
    // the raw first paint of step 2 and prevents the content flash.
    const [isAutoApplyArmed, setAutoApplyArmed] = useState(false);
    useEffect(() => {
      setAutoApplyArmed(isDefaultAutoApplyPending);
    }, [isDefaultAutoApplyPending]);

    // Settled-context guard for the default auto-apply on a currency switch.
    // Mirrors the Case-1 revalidation guard in components/Discount/func. A currency
    // switch produces a transient rederive cycle whose committed derive key already
    // carries the new currency while the rate/receive amount are still the previous
    // currency's (the rate is fetched async and lags the currency selection). Both
    // the primary and backup guards target that stale cycle:
    //
    //  * PRIMARY `isCalculatorRounding` — true while the calculator is recomputing
    //    the amount for the new rate. Runtime-confirmed true on the stale cycle and
    //    false on the settled cycle for this default-auto-apply flow, so it is the
    //    reliable discriminator here. Without it the default validates twice on a
    //    currency change (once against the stale amount), because the two rederive
    //    cycles each re-arm auto-apply. (func:946 uses the same guard.)
    //  * BACKUP `amountSettledForRate` (recv ~= send * rate) — defends the other
    //    observed ordering where the rate updates BEFORE the receive amount, making
    //    the stale pair inconsistent. It CANNOT catch the ordering above (stale
    //    rate + stale amount are a self-consistent pair), which is why the primary
    //    guard is needed. Amounts come from the committed derive key — the exact
    //    context discountsActive/discountDefault correspond to; see
    //    buildTransactionDiscountDeriveKey (…, isOnlyCurrency[3], send[6], recv[7]);
    //    the rate is from the store. No-op unless single-currency with a numeric
    //    rate/amount, so FX / add-another-currency / initial-load are unchanged.
    const autoApplyInfo = isAddTransactionPage ? toInformation : fxInformation;
    const autoApplyDeriveParts = String(
      autoApplyInfo?.discountDeriveKey || '',
    ).split('|');
    const autoApplyIsOnlyCurrency = autoApplyDeriveParts[3] === 'true';
    const autoApplySendAmount = Number(autoApplyDeriveParts[6] || 0) || 0;
    const autoApplyReceiveAmount = Number(autoApplyDeriveParts[7] || 0) || 0;
    const autoApplyRateValue =
      Number(autoApplyInfo?.remittance?.currentRateStore?.rateValue || 0) || 0;
    const isAutoApplyAmountSettledForRate =
      !autoApplyIsOnlyCurrency ||
      autoApplyRateValue <= 0 ||
      autoApplyReceiveAmount <= 0 ||
      Math.abs(
        autoApplyReceiveAmount - autoApplySendAmount * autoApplyRateValue,
      ) <= autoApplyReceiveAmount * 0.1;

    useEffect(() => {
      if (isDisabled) return;
      if (discountApplyRef.current) return;
      if (!isAutoApplyArmed) return;
      if (isAutoApplyReady === false) return;
      if (isAutoApplyBlocked) return;
      // Skip the transient unsettled rederive cycle of a currency change; fire the
      // default validation once at the settled context (see the guard above).
      if (isCalculatorRounding) return;
      if (!isAutoApplyAmountSettledForRate) return;
      if (isEmpty(discountDefault)) return;
      if (isEmpty(discountsAvailable) && isEmpty(discountsInactive)) return;
      if (!isEmpty(discountApplied)) return;
      discountApplyRef.current = true;
      handleApplyDiscount({
        token,
        item: discountDefault,
        onApplyError: (errorCode, item) => {
          discountApplyRef.current = false;
          handleAutoApplyError?.(errorCode, item || discountDefault);
        },
        onApplySuccess: () => {
          discountApplyRef.current = false;
        },
      });
    }, [
      isDisabled,
      isAutoApplyArmed,
      discountDefault,
      discountsAvailable,
      discountApplied,
      discountsInactive,
      isAutoApplyReady,
      isAutoApplyBlocked,
      isCalculatorRounding,
      isAutoApplyAmountSettledForRate,
      handleAutoApplyError,
    ]);

    const newProps = {
      ...props,
      failedDiscountRecovery: failedDiscountRecovery
        ? {
            ...failedDiscountRecovery,
            // Show the loading placeholder for the whole default auto-apply
            // window (loaded -> applying -> resolved) so the discount section
            // stays hidden until validation completes, instead of flashing
            // content beforehand.
            isValidating:
              Boolean(failedDiscountRecovery.isValidating) ||
              isDefaultAutoApplyPending,
          }
        : failedDiscountRecovery,
    };
    return <Component {...newProps} />;
  };
  return DiscountAutoApplyComponent;
};

export default withDiscountAutoApply;
